import React, { useLayoutEffect, useState } from "react";

interface SectionRefs {
  stakeholders: React.RefObject<HTMLDivElement | null>;
  topics: React.RefObject<HTMLDivElement | null>;
  iro: React.RefObject<HTMLDivElement | null>;
  questions: React.RefObject<HTMLDivElement | null>;
}

interface ConnectorsOverlayProps {
  containerRef: React.RefObject<HTMLDivElement | null>;
  sectionRefs: SectionRefs;
  stroke: string;
  topicsCount: number;
  iroCount: number;
  questionsCount: number;
}

const ConnectorsOverlay: React.FC<ConnectorsOverlayProps> = ({
  containerRef,
  sectionRefs,
  stroke,
  topicsCount,
  iroCount,
  questionsCount,
}) => {
  const [size, setSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });
  const [paths, setPaths] = useState<string[]>([]);
  const cornerRadius = 15;

  const computeRowCenters = (
    sectionEl: HTMLDivElement | null,
    skipHeader: boolean,
  ): number[] => {
    if (!sectionEl) return [];
    const containerTop = containerRef.current?.getBoundingClientRect().top ?? 0;
    const rows = Array.from(sectionEl.querySelectorAll("tbody tr"));

    if (rows.length === 0) return [];
    const startIdx = skipHeader ? 1 : 0;

    return rows.slice(startIdx).map((tr) => {
      const rect = tr.getBoundingClientRect();

      return rect.top - containerTop + rect.height / 2;
    });
  };

  const computeRowCentersFallback = (
    sectionEl: HTMLDivElement | null,
    skipHeader: boolean,
    fallbackCount: number,
  ): number[] => {
    const direct = computeRowCenters(sectionEl, skipHeader);

    if (direct.length > 0 || !sectionEl || fallbackCount <= 0) return direct;
    const containerTop = containerRef.current?.getBoundingClientRect().top ?? 0;
    const rect = sectionEl.getBoundingClientRect();
    const totalSlots = fallbackCount + (skipHeader ? 1 : 0);

    if (totalSlots <= 0 || rect.height <= 0) return [];
    const slotHeight = rect.height / totalSlots;
    const startIndex = skipHeader ? 1 : 0;

    return Array.from({ length: fallbackCount }, (_, idx) => {
      const slotIndex = startIndex + idx;
      const centerY = rect.top + slotHeight * (slotIndex + 0.5);

      return centerY - containerTop;
    });
  };

  const recompute = (): void => {
    const containerRect = containerRef.current?.getBoundingClientRect();

    if (!containerRect) return;
    setSize({ width: containerRect.width, height: containerRect.height });

    const stakeholdersRect =
      sectionRefs.stakeholders.current?.getBoundingClientRect();
    const topicsRect = sectionRefs.topics.current?.getBoundingClientRect();
    const iroRect = sectionRefs.iro.current?.getBoundingClientRect();
    const questionsRect = sectionRefs.questions.current?.getBoundingClientRect();

    const containerLeft = containerRect.left;
    const containerTop = containerRect.top;

    const svgPaths: string[] = [];

    if (stakeholdersRect && topicsRect) {
      const trunkX = topicsRect.left - containerLeft - 12;
      const trunkY1 = stakeholdersRect.bottom - containerTop;
      const trunkY2 = topicsRect.bottom - containerTop - 40;
      const armsY = computeRowCentersFallback(
        sectionRefs.topics.current,
        true,
        topicsCount,
      );
      const armX2 = topicsRect.left - containerLeft + 24;

      svgPaths.push(`M ${trunkX} ${trunkY1} V ${trunkY2}`);
      armsY.forEach((y) => {
        const startY = y - cornerRadius;
        const arcPath = `a ${cornerRadius} ${cornerRadius} 0 0 0 ${cornerRadius} ${cornerRadius}`;

        svgPaths.push(`M ${trunkX} ${startY} ${arcPath} H ${armX2}`);
      });
    }

    if (topicsRect && iroRect) {
      const trunkX = iroRect.left - containerLeft - 12;
      const trunkY1 = topicsRect.bottom - containerTop;
      const trunkY2 = iroRect.bottom - containerTop - 40;
      const armsY = computeRowCentersFallback(
        sectionRefs.iro.current,
        true,
        iroCount,
      );
      const armX2 = iroRect.left - containerLeft + 24;

      svgPaths.push(`M ${trunkX} ${trunkY1} V ${trunkY2}`);
      armsY.forEach((y) => {
        const startY = y - cornerRadius;
        const arcPath = `a ${cornerRadius} ${cornerRadius} 0 0 0 ${cornerRadius} ${cornerRadius}`;

        svgPaths.push(`M ${trunkX} ${startY} ${arcPath} H ${armX2}`);
      });
    }

    if (iroRect && questionsRect) {
      const trunkX = questionsRect.left - containerLeft - 12;
      const trunkY1 = iroRect.bottom - containerTop;
      const trunkY2 = questionsRect.bottom - containerTop - 40;
      const armsY = computeRowCentersFallback(
        sectionRefs.questions.current,
        false,
        questionsCount,
      );
      const armX2 = questionsRect.left - containerLeft + 24;

      svgPaths.push(`M ${trunkX} ${trunkY1} V ${trunkY2}`);
      armsY.forEach((y) => {
        const startY = y - cornerRadius;
        const arcPath = `a ${cornerRadius} ${cornerRadius} 0 0 0 ${cornerRadius} ${cornerRadius}`;

        svgPaths.push(`M ${trunkX} ${startY} ${arcPath} H ${armX2}`);
      });
    }

    setPaths(svgPaths);
  };

  useLayoutEffect(() => {
    const schedule = (): void => {
      requestAnimationFrame(() => requestAnimationFrame(recompute));
    };

    const containerObs = new ResizeObserver(() => schedule());
    const sectionObs = new ResizeObserver(() => schedule());

    if (containerRef.current) containerObs.observe(containerRef.current);
    if (sectionRefs.stakeholders.current)
      sectionObs.observe(sectionRefs.stakeholders.current);
    if (sectionRefs.topics.current) sectionObs.observe(sectionRefs.topics.current);
    if (sectionRefs.iro.current) sectionObs.observe(sectionRefs.iro.current);
    if (sectionRefs.questions.current)
      sectionObs.observe(sectionRefs.questions.current);

    schedule();

    window.addEventListener("resize", schedule);

    return () => {
      containerObs.disconnect();
      sectionObs.disconnect();
      window.removeEventListener("resize", schedule);
    };
  }, [topicsCount, iroCount, questionsCount]);


  if (questionsCount === 0) {
    return null;
  }

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: "none",
        zIndex: 0,
      }}
    >
      {size.height > 0 && (
        <svg
          width={size.width}
          height={size.height}
          style={{
            position: "absolute",
            top: 0,
            left: -18,
            pointerEvents: "none",
            zIndex: 0,
          }}
        >
          {paths.map((d, idx) => (
            <path key={idx} d={d} stroke={stroke} strokeWidth={1} fill="none" />
          ))}
        </svg>
      )}
    </div>
  );
};

export default ConnectorsOverlay;
